// frontend/src/features/ups/UpsDashboard.js
// This React layout renders the UPS Admin dashboard—a collapsible sidebar with navigation, global search & filters, driver data hydration, stats at the top, and nested routes for drivers, accounts, and agencies.

import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  LayoutList,
  Ban as BannedIcon,
  Users,
  Building2,
  Menu,
  X,
  UserCheck,
} from 'lucide-react';

import UpsDriverStats from './UpsDriverStats';

// Global filters
import { selectAgencyId, selectFilters, setScope, setQuery } from '../ui/filtersSlice';

// Centralized fetch on agency change
import { upsertDrivers } from '../drivers/driverSlice';
import { fetchAllStatuses } from '../../services/upsDriverService';

// Global search drawer
import GlobalResultsDrawer from '../../components/GlobalResultsDrawer';

const statusToRoute = {
  Active:   '/ups-admin/drivers',
  Reserved: '/ups-admin/drivers/reserved',
  Banned:   '/ups-admin/drivers/banned',
};

export default function UpsDashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const btnBase = 'block w-full text-left px-4 py-2 rounded transition-all';

  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const agencyId    = useSelector(selectAgencyId); // '' = All Agencies
  const { q } = useSelector(selectFilters);

  // ✅ Call hooks unconditionally, then compute the boolean
  const matchDriversRoot = useMatch('/ups-admin/drivers');
  const matchDriversAny  = useMatch('/ups-admin/drivers/*');
  const inDriversSection = Boolean(matchDriversRoot || matchDriversAny);

  // Fetch Active + Reserved + Banned whenever agency changes (only on driver routes)
  useEffect(() => {
    if (!inDriversSection) return;
    let ignore = false;
    (async () => {
      try {
        const { active, reserved, banned } = await fetchAllStatuses({
          agencyId: agencyId ? Number(agencyId) : undefined,
        });
        if (!ignore) {
          dispatch(upsertDrivers([...active, ...reserved, ...banned]));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[UPS Dashboard] fetchAllStatuses failed', err);
      }
    })();
    return () => { ignore = true; };
  }, [dispatch, agencyId, inDriversSection]);

  // Clear global search + scope when leaving the drivers section
  useEffect(() => {
    if (!inDriversSection) {
      dispatch(setScope('tab'));
      dispatch(setQuery(''));
    }
  }, [inDriversSection, dispatch]);

  // Handlers for the drawer
  const openResultInTab = (row) => {
    const route = statusToRoute[row.status] || '/ups-admin/drivers';
    dispatch(setScope('tab'));
    const params = new URLSearchParams();
    if (agencyId) params.set('agencyId', agencyId);
    if (q)        params.set('q', q);
    params.set('focus', String(row.id));
    navigate(`${route}?${params.toString()}`);
  };

  const viewAllForStatus = (status) => {
    const route = statusToRoute[status] || '/ups-admin/drivers';
    dispatch(setScope('tab'));
    const params = new URLSearchParams();
    if (agencyId) params.set('agencyId', agencyId);
    if (q)        params.set('q', q);
    navigate(`${route}?${params.toString()}`);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-64'} bg-white border-r shadow-sm flex flex-col transition-all duration-200`}>
        <div className="p-4 flex items-center justify-between">
          {!collapsed && <h2 className="text-xl font-bold">UPS Admin</h2>}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-gray-200"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <Menu size={20} /> : <X size={20} />}
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-1">
          <NavLink
            to="/ups-admin/drivers"
            end
            className={({ isActive }) =>
              `${btnBase} ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} flex items-center`
            }
          >
            <LayoutList className="mr-2" />
            {!collapsed && 'Organized Drivers'}
          </NavLink>

          <NavLink
            to="/ups-admin/drivers/reserved"
            className={({ isActive }) =>
              `${btnBase} ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} flex items-center`
            }
          >
            <UserCheck className="mr-2" />
            {!collapsed && 'Reserved Drivers'}
          </NavLink>

          <NavLink
            to="/ups-admin/drivers/banned"
            className={({ isActive }) =>
              `${btnBase} ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} flex items-center`
            }
          >
            <BannedIcon className="mr-2" />
            {!collapsed && 'Banned Drivers'}
          </NavLink>

          <NavLink
            to="/ups-admin/accounts"
            className={({ isActive }) =>
              `${btnBase} ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} flex items-center`
            }
          >
            <Users className="mr-2" />
            {!collapsed && 'Manage Accounts'}
          </NavLink>

           <NavLink
             to="/ups-admin/manage-agencies"
             className={({ isActive }) =>
             `${btnBase} ${isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'} flex items-center`
             }
           >
             <Building2 className="mr-2" />
             {!collapsed && 'Manage Agencies'}
             </NavLink>
        </nav>
      </aside>

      {/* Main area */}
      <main className="relative flex-1 overflow-y-auto">
        <div className="p-6">
        {inDriversSection && <UpsDriverStats />}
        {inDriversSection && (
          <GlobalResultsDrawer
            onOpen={openResultInTab}
            onViewAll={viewAllForStatus}
          />
        )}
        <Outlet />
        </div>
      </main>
    </div>
  );
}
